import {
  DEFAULT_CURRENCY_CODE,
  getStaticCurrencyByCode,
} from './currencies'
import { TInvoiceForm } from '../types'
import { normalizeDraftItems } from './invoiceDraftItems'
import { saveNewInvoiceDraft } from './invoiceDraftStorage'

export const GUEST_INVOICE_DRAFT_STORAGE_KEY = 'invoice:guest:draft'
export const GUEST_INVOICE_READY_PROMPT_SESSION_KEY = 'invoice:guest:ready-prompt'
export const GUEST_INVOICE_DRAFT_VERSION = 1

export type GuestPendingIntent = 'send' | 'viewPdf'

export type GuestInvoiceReadyPromptSignal = {
  invoiceId: string
  pendingIntent: GuestPendingIntent
  profileOverwriteResolved?: boolean
}

export type DraftClient = {
  tradingName: string
  email: string
  billingAddress: string
  name?: string
  lastName?: string
}

export type GuestInvoiceDraftPayload = {
  version: number
  values: TInvoiceForm
  draftClient: DraftClient
  logoSrc?: string
  paymentInstructionsDraft?: string
  pendingIntent?: GuestPendingIntent
}

export const EMPTY_DRAFT_CLIENT: DraftClient = {
  tradingName: '',
  email: '',
  billingAddress: '',
  name: '',
  lastName: '',
}

export const getDefaultGuestInvoiceValues = (): TInvoiceForm => {
  const staticCurrency = getStaticCurrencyByCode(DEFAULT_CURRENCY_CODE)!
  return {
    items: [],
    issueDate: new Date(),
    dueDate: new Date(new Date().setDate(new Date().getDate() + 7)),
    locked: false,
    code: 'INV-0001',
    businessDetails: {
      name: '',
      email: '',
      enableTax: false,
      tax: { name: '', percentage: 0 },
      currency: {
        _id: staticCurrency.code,
        name: staticCurrency.name,
        code: staticCurrency.code,
        symbol: staticCurrency.symbol,
      },
    },
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object'

export const parseDraftClient = (value: unknown): DraftClient | null => {
  if (!isRecord(value)) return null
  const tradingName = typeof value.tradingName === 'string' ? value.tradingName : ''
  const email = typeof value.email === 'string' ? value.email : ''
  const billingAddress = typeof value.billingAddress === 'string' ? value.billingAddress : ''
  return {
    tradingName,
    email,
    billingAddress,
    name: typeof value.name === 'string' ? value.name : '',
    lastName: typeof value.lastName === 'string' ? value.lastName : '',
  }
}

const parsePendingIntent = (value: unknown): GuestPendingIntent | undefined =>
  value === 'send' || value === 'viewPdf' ? value : undefined

const reviveDate = (value: unknown, fallback: Date): Date => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value
  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) return parsed
  }
  return fallback
}

const parseGuestInvoiceValues = (
  value: unknown,
  fallbackValues: TInvoiceForm,
): TInvoiceForm | null => {
  if (!isRecord(value)) return null
  const issueDate = reviveDate(value.issueDate, fallbackValues.issueDate)
  const dueDate = reviveDate(value.dueDate, fallbackValues.dueDate)
  const businessDetails = isRecord(value.businessDetails)
    ? { ...fallbackValues.businessDetails, ...value.businessDetails }
    : fallbackValues.businessDetails
  const items = normalizeDraftItems(
    Array.isArray(value.items) ? value.items : fallbackValues.items,
  )
  return {
    ...fallbackValues,
    ...value,
    issueDate,
    dueDate,
    businessDetails,
    items,
    _id: undefined,
    clientId: undefined,
    locked: false,
  }
}

export const parseGuestInvoiceDraft = (
  raw: unknown,
  fallbackValues: TInvoiceForm = getDefaultGuestInvoiceValues(),
): GuestInvoiceDraftPayload | null => {
  if (!isRecord(raw)) return null
  const values = parseGuestInvoiceValues(raw.values, fallbackValues)
  const draftClient = parseDraftClient(raw.draftClient)
  if (!values || !draftClient) return null
  const version = typeof raw.version === 'number' ? raw.version : GUEST_INVOICE_DRAFT_VERSION
  const logoSrc = typeof raw.logoSrc === 'string' ? raw.logoSrc : undefined
  const paymentInstructionsDraft =
    typeof raw.paymentInstructionsDraft === 'string' ? raw.paymentInstructionsDraft : undefined
  const pendingIntent = parsePendingIntent(raw.pendingIntent)
  return {
    version,
    values,
    draftClient,
    logoSrc,
    paymentInstructionsDraft,
    pendingIntent,
  }
}

export const loadGuestInvoiceDraft = (
  fallbackValues: TInvoiceForm = getDefaultGuestInvoiceValues(),
): GuestInvoiceDraftPayload | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(GUEST_INVOICE_DRAFT_STORAGE_KEY)
    if (!raw) return null
    return parseGuestInvoiceDraft(JSON.parse(raw), fallbackValues)
  } catch {
    return null
  }
}

export const saveGuestInvoiceDraft = (draft: GuestInvoiceDraftPayload): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(
      GUEST_INVOICE_DRAFT_STORAGE_KEY,
      JSON.stringify({
        ...draft,
        version: GUEST_INVOICE_DRAFT_VERSION,
        values: { ...draft.values, items: normalizeDraftItems(draft.values.items) },
      }),
    )
  } catch {
    // ignore quota / private mode errors
  }
}

export const clearGuestInvoiceDraftStorage = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(GUEST_INVOICE_DRAFT_STORAGE_KEY)
  } catch {
    // ignore storage errors
  }
}

export const setGuestPendingIntent = (intent: GuestPendingIntent): boolean => {
  const draft = loadGuestInvoiceDraft()
  if (!draft) return false
  saveGuestInvoiceDraft({ ...draft, pendingIntent: intent })
  return true
}

export const clearGuestPendingIntent = (): void => {
  const draft = loadGuestInvoiceDraft()
  if (!draft?.pendingIntent) return
  saveGuestInvoiceDraft({ ...draft, pendingIntent: undefined })
}

export const hasGuestPendingIntent = (): boolean =>
  loadGuestInvoiceDraft()?.pendingIntent != null

export const writeGuestInvoiceReadyPromptSignal = (
  signal: GuestInvoiceReadyPromptSignal,
): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(
      GUEST_INVOICE_READY_PROMPT_SESSION_KEY,
      JSON.stringify(signal),
    )
  } catch {
    // ignore quota / private mode errors
  }
}

export const readGuestInvoiceReadyPromptSignal = (): GuestInvoiceReadyPromptSignal | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(GUEST_INVOICE_READY_PROMPT_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!isRecord(parsed)) return null
    const invoiceId = typeof parsed.invoiceId === 'string' ? parsed.invoiceId : ''
    const pendingIntent = parsePendingIntent(parsed.pendingIntent)
    if (!invoiceId || !pendingIntent) return null
    return {
      invoiceId,
      pendingIntent,
      ...(parsed.profileOverwriteResolved === true
        ? { profileOverwriteResolved: true }
        : {}),
    }
  } catch {
    return null
  }
}

export const markGuestProfileOverwriteResolved = (): void => {
  const signal = readGuestInvoiceReadyPromptSignal()
  if (!signal) return
  writeGuestInvoiceReadyPromptSignal({
    ...signal,
    profileOverwriteResolved: true,
  })
}

export const clearGuestInvoiceReadyPromptSignal = (): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(GUEST_INVOICE_READY_PROMPT_SESSION_KEY)
  } catch {
    // ignore storage errors
  }
}

export const persistHydrationFailureFallback = (fallback: {
  guestDraftToKeep: GuestInvoiceDraftPayload
  authenticatedDraft: {
    values: TInvoiceForm
    paymentInstructionsDraft: string
    draftClient: DraftClient
  }
}): void => {
  saveGuestInvoiceDraft(fallback.guestDraftToKeep)
  saveNewInvoiceDraft(fallback.authenticatedDraft)
}
